import { __assign, __rest } from "tslib";
import React, { useCallback, useContext, useEffect, useRef, useState, } from 'react';
import c from 'classnames';
import ReactModal from 'react-modal';
import { ThemeOptions, ThemeOverride, } from '@wix/da-react-context/pkg/ThemeContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useBodyScrollLock, } from '../../../hooks/useBodyScrollLock';
import { useVisualViewport } from '../../../hooks/useVisualViewport';
import IconButton from '../../Buttons/IconButton';
import CloseBold from '../../Icons/CloseBold';
import s from './ModalRoot.scss';
/**
 * The root component of any modal. This is a wrapper for `react-popper`'s `<ReactPopper />` component.
 *
 * Provides theming, body scroll locking, and some additional styling.
 */
var ModalRoot = function (_a) {
    var className = _a.className, _b = _a.matchTheme, matchTheme = _b === void 0 ? false : _b, width = _a.width, height = _a.height, useFixedMobileHeight = _a.useFixedMobileHeight, bodyScrollLockOptions = _a.bodyScrollLockOptions, onRequestClose = _a.onRequestClose, children = _a.children, rest = __rest(_a, ["className", "matchTheme", "width", "height", "useFixedMobileHeight", "bodyScrollLockOptions", "onRequestClose", "children"]);
    var isMobile = useContext(MobileContext);
    // Mobile fixed to viewport fixes
    var _c = useState(), visualViewport = _c[0], setVisualViewport = _c[1];
    var viewportHandler = useCallback(function (v) {
        setVisualViewport(v);
    }, []);
    useVisualViewport(viewportHandler, isMobile && !!useFixedMobileHeight);
    var style = {
        content: {
            width: width,
            height: height,
        },
    };
    var contentStyle = {};
    // Apply fixed height for mobile if requested, helper for fixing Safari being lame
    if (isMobile && visualViewport && !height) {
        style.content.height = visualViewport.height;
        style.content.transform = "translate(" + visualViewport.offsetLeft + "px," + visualViewport.offsetTop + "px)";
        style.content.scale = visualViewport.scale;
        // Calculate positions from top left
        style.content.top = 0;
        style.content.left = 0;
        style.content.position = 'fixed';
        // Set the max height of the content, fixes content expanding beyond viewport
        contentStyle.maxHeight = visualViewport.height;
    }
    // Mobile body scroll locking
    var contentRef = useRef(null);
    var _d = useBodyScrollLock(bodyScrollLockOptions), onLock = _d.onLock, onUnlock = _d.onUnlock, setScrollableRef = _d.setScrollableRef, scrollableElementRef = _d.scrollableElementRef;
    useEffect(function () {
        var focusedEl = document.activeElement;
        var contentEl = contentRef.current;
        var isFocused = function (el) { return el === focusedEl || el.contains(focusedEl); };
        var needsFocus = contentEl && (!focusedEl || !isFocused(contentEl));
        var focusTimeout;
        if (needsFocus) {
            // useEffect is supposed to be invoked when everything mounted, but without this timeout something else steals the focus
            focusTimeout = setTimeout(function () { return contentEl.focus(); }, 100);
        }
        return function () { return focusTimeout && clearTimeout(focusTimeout); };
    }, []);
    var setContentRef = function (instance) {
        contentRef.current = instance;
        // It might happen that the Popup renders a component that calls setScrollableRef on its own, such as ScrollableContent.
        // In this case we avoid updating the ref because that would cause an incorrect re-lock to the modal content element.
        if (!scrollableElementRef.current) {
            setScrollableRef(instance);
        }
    };
    var handleClose = useCallback(function (e) {
        onUnlock();
        onRequestClose && onRequestClose(e);
    }, [onRequestClose, onUnlock]);
    return (React.createElement(ReactModal, __assign({ className: c(s['modal'], className), overlayClassName: s['overlay'], portalClassName: 'ReactModalPortal', style: style, contentRef: setContentRef, onAfterOpen: onLock, onRequestClose: handleClose }, rest), matchTheme ? (React.createElement(React.Fragment, null,
        children,
        React.createElement(IconButton, { className: s['close-button'], size: "medium", icon: CloseBold, onClick: handleClose }))) : (React.createElement(ThemeOverride, { theme: ThemeOptions.LIGHT },
        children,
        React.createElement(IconButton, { className: s['close-button'], size: "medium", icon: CloseBold, onClick: handleClose })))));
};
ModalRoot.displayName = 'ModalRoot';
export default ModalRoot;
//# sourceMappingURL=ModalRoot.js.map